import { Wallet } from '@dynamic-labs/sdk-react-core'
import { isEthereumWallet } from '@dynamic-labs/ethereum'
import { Address } from 'viem'
import { whypeConfig } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { trackEvent } from '@/utils/fathom'
import { SwapMode } from './types'
import { PageState } from '@/features/dialogs/common/types'
import { marketBalancesQueryKey } from '@/domain/wallet/marketBalances'
import { queryClient } from '@/config/query-client'

export interface WrapUnwrap {
  primaryWallet: Wallet | null
  chainId: number
  value: bigint
  swapMode: SwapMode
  setSwapState: (state: string) => void
  setSwapError: (error: boolean) => void
  setPageStatus: (status: PageState) => void
}

export async function wrapUnwrap({
  primaryWallet,
  chainId,
  value,
  swapMode,
  setSwapState,
  setSwapError,
  setPageStatus,
}: WrapUnwrap): Promise<void> {
  if (!primaryWallet || !isEthereumWallet(primaryWallet) || !primaryWallet.address) {
    console.warn('Wallet not available or not an Ethereum wallet')
    return
  }

  if (swapMode === 'swap') {
    console.warn('Swap mode not supported')
    return
  }

  try {
    setSwapState('Initializing...')
    setSwapError(false)

    const account = primaryWallet.address as Address
    const publicClient = await primaryWallet.getPublicClient()
    const walletClient = await primaryWallet.getWalletClient()

    const whypeContractAddress = getContractAddress(whypeConfig.address, chainId)

    if (swapMode === 'wrap') {
      setSwapState('Wrapping...')
      const simulation = await publicClient.simulateContract({
        address: whypeContractAddress,
        account,
        abi: whypeConfig.abi,
        functionName: 'deposit',
        value,
      })

      const hash = await walletClient.writeContract(simulation.request)
      await publicClient.waitForTransactionReceipt({ hash })
    } else {
      setSwapState('Unwrapping...')
      const simulation = await publicClient.simulateContract({
        address: whypeContractAddress,
        account,
        abi: whypeConfig.abi,
        functionName: 'withdraw',
        args: [value],
      })

      const hash = await walletClient.writeContract(simulation.request)
      await publicClient.waitForTransactionReceipt({ hash })
    }

    void queryClient.invalidateQueries({
      queryKey: marketBalancesQueryKey({ account, chainId }),
    })

    trackEvent(swapMode === 'wrap' ? 'action_wrap_hype_to_whype_success' : 'action_unwrap_whype_to_hype_success')
    setSwapState('')
    setPageStatus('success')
  } catch (error) {
    if (error instanceof Error && error.message.includes('User rejected')) return

    setSwapError(true)
    console.error(error)
    trackEvent(swapMode === 'wrap' ? 'action_wrap_hype_to_whype_failure' : 'action_unwrap_whype_to_hype_failure')
  } finally {
    setSwapState('')
  }
}
